import { Head, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import type ProductsIndex from "@travily/types/pages/ProductsIndex";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { SearchRoot } from "@/components/Search/SearchRoot";
import { useFavoritedIdsCallbacks } from "@/hooks/use-search-favorites";
import { useSearchSessionUiState } from "@/hooks/use-search-session";
import { ExploreFiltersProvider } from "@/layouts/components/explore-filters-context";
import { PageFrame } from "@/layouts/page-frame";
import { analyticsEvents } from "@/lib/analytics-events";
import { IndexView } from "./index.view";
import {
  MarketplaceFiltersAdapter,
  MarketplaceSearchAdapter,
  MarketplaceSearchBarAdapter,
} from "./MarketplaceSearchAdapter";
import { MarketplaceSortProvider } from "./MarketplaceSortContext";
import { MarketplaceUrlSync } from "./MarketplaceUrlSync";
import { hasMarketplaceSearchParams, parseMarketplaceSearchParams, toMarketplaceUiState } from "./search-params";
import { useMarketplaceSearch } from "./useMarketplaceSearch";

const MarketplaceIndexPage = ({ favorited_ids, seed_results, seed_total }: ProductsIndex) => {
  const search = useMarketplaceSearch();
  const posthog = usePostHog();
  useEffect(() => {
    posthog.capture(analyticsEvents.marketplaceViewed);
  }, [posthog]);
  const { getFavorited, onFavoriteToggle } = useFavoritedIdsCallbacks(favorited_ids);
  const pageUrl = usePage().url;
  // Parsed once per mount: shared links, back/forward (Inertia remounts on
  // popstate), and the mobile overlay hand-off all rehydrate through it. A URL
  // carrying any search param wins over the restored session; MarketplaceUrlSync
  // keeps the URL current from then on.
  const [initialSearch] = useState(() => parseMarketplaceSearchParams(pageUrl));
  const { initialUiState, onStateChange } = useSearchSessionUiState("marketplace", {
    urlUiState: toMarketplaceUiState(initialSearch, search.indexName),
  });
  // Seed only the untouched default page: a shared link carrying search params
  // would flash the default grid before its own results land.
  const [seedListings] = useState(() => (hasMarketplaceSearchParams(pageUrl) ? [] : seed_results));
  const [seedTotal] = useState(() => (hasMarketplaceSearchParams(pageUrl) ? null : seed_total));

  return (
    <SearchRoot initialUiState={initialUiState} onStateChange={onStateChange} search={search}>
      <MarketplaceUrlSync />
      <ExploreFiltersProvider filters={<MarketplaceFiltersAdapter />}>
        <MarketplaceSortProvider>
          <PageFrame
            contentMaxWidth="full"
            contentPadded={false}
            footerFullBleed
            topBarBottomContent={<IndexView searchBar={<MarketplaceSearchBarAdapter />} />}
          >
            <Head title={t("web.marketplace.index.meta_title")}>
              <meta name="description" content={t("web.marketplace.index.meta_description")} />
            </Head>
            <div className="p-5 md:p-8 md:pt-6">
              <MarketplaceSearchAdapter
                getFavorited={getFavorited}
                onFavoriteToggle={onFavoriteToggle}
                seedListings={seedListings}
                seedTotal={seedTotal}
              />
            </div>
          </PageFrame>
        </MarketplaceSortProvider>
      </ExploreFiltersProvider>
    </SearchRoot>
  );
};

export default MarketplaceIndexPage;
